import { required } from 'vuelidate/lib/validators';
import {
  addMonthsSafe,
  businessDaysBetween,
  dateToFormIsoString,
  parseIsoDateLocal,
} from '@/utils/format';

export { addMonthsSafe, businessDaysBetween, dateToFormIsoString, parseIsoDateLocal };

export const TIPO_DOCUMENTO_ARP_PRINCIPAL = 11;
export const TIPO_PARTE_GERENCIADORA = 1;

/** Ano da ARP: apenas positivos, de 1999 a 9999. */
export const ARP_ANO_MIN = 1999;
export const ARP_ANO_MAX = 9999;

export function isAnoArpValido(value) {
  const ano = Number(value);
  if (!Number.isFinite(ano) || !Number.isInteger(ano)) return false;
  return ano >= ARP_ANO_MIN && ano <= ARP_ANO_MAX;
}

/** Vigência início→fim: no máximo `maxMeses` meses civis (inclusive no limite). */
export function isVigenciaArpDentroDoLimite(inicio, fim, maxMeses = 12) {
  const ini = parseIsoDateLocal(inicio);
  const fimD = parseIsoDateLocal(fim);
  if (!ini || !fimD) return false;
  if (fimD.getTime() <= ini.getTime()) return false;
  const limite = addMonthsSafe(ini, maxMeses);
  return fimD.getTime() <= limite.getTime();
}

/** Normaliza input do campo Ano (rejeita negativo/fora da faixa → string vazia ou clipping). */
export function normalizeAnoArpInput(value) {
  if (value === '' || value == null) return '';
  const digits = String(value).replace(/[^\d]/g, '');
  if (!digits) return '';
  const ano = Number(digits);
  if (!Number.isFinite(ano)) return '';
  if (ano > ARP_ANO_MAX) return String(ARP_ANO_MAX);
  return String(ano);
}

export {
  PNCP_DOCUMENT_UPLOAD_LIMIT_BYTES,
  PNCP_DOCUMENT_UPLOAD_MAX_MB,
  PNCP_DOCUMENT_UPLOAD_TOOLTIP,
  getPncpDocumentUploadSizeErrorMessage,
  isWithinPncpDocumentUploadLimit,
} from '@/utils/pncpDocumentUpload';

export function startOfToday() {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return t;
}

export function arquivoArpObrigatorio(value) {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  return typeof value === 'object' && !!value.name;
}

export function createArpInclusaoValidators() {
  const isDate = v => parseIsoDateLocal(v) != null;
  const assinaturaNaoFutura = v => {
    const d = parseIsoDateLocal(v);
    if (!d) return true;
    return d.getTime() <= startOfToday().getTime();
  };

  const vigenciaInicioDepoisOuIgualAssinatura = (v, vm) => {
    const ini = parseIsoDateLocal(v);
    const ass = parseIsoDateLocal(vm?.dataAssinatura);
    if (!ini || !ass) return true;
    return ini.getTime() >= ass.getTime();
  };

  const vigenciaFimDepoisDoInicio = (v, vm) => {
    const fim = parseIsoDateLocal(v);
    const ini = parseIsoDateLocal(vm?.dataVigenciaInicio);
    if (!fim || !ini) return true;
    return fim.getTime() > ini.getTime();
  };

  const maxVigenciaMeses = maxMeses => (v, vm) => {
    const fim = parseIsoDateLocal(v);
    const ass = parseIsoDateLocal(vm?.dataAssinatura);
    if (!fim || !ass) return true;
    const limite = addMonthsSafe(ass, maxMeses);
    return fim.getTime() <= limite.getTime();
  };

  return {
    arquivo: { arquivoArpObrigatorio },
    numeroAtaRegistroPreco: { required },
    anoAta: { required, isAnoArpValido },
    dataAssinatura: {
      required,
      isDate,
      assinaturaNaoFutura,
    },
    dataVigenciaInicio: {
      required,
      isDate,
      vigenciaInicioDepoisOuIgualAssinatura,
    },
    dataVigenciaFim: {
      required,
      isDate,
      vigenciaFimDepoisDoInicio,
      maxVigencia12Meses: maxVigenciaMeses(12),
    },
  };
}

export function createArpMetadadosValidators() {
  const validators = createArpInclusaoValidators();
  const { arquivo, ...metadados } = validators;
  return metadados;
}

export function createArpRetificacaoValidators() {
  const metadados = createArpMetadadosValidators();
  const maxVigenciaMeses = maxMeses => (v, vm) => {
    const fim = parseIsoDateLocal(v);
    const ass = parseIsoDateLocal(vm?.dataAssinatura);
    if (!fim || !ass) return true;
    const limite = addMonthsSafe(ass, maxMeses);
    return fim.getTime() <= limite.getTime();
  };

  const { maxVigencia12Meses, ...vigenciaFimRules } = metadados.dataVigenciaFim;

  return {
    ...metadados,
    dataVigenciaFim: {
      ...vigenciaFimRules,
      maxVigencia24Meses: maxVigenciaMeses(24),
    },
  };
}

export function getMensagemCampoArp(v, field, validacao = 'dados') {
  if (!v || !v[field] || !v[field].$dirty) return '';

  const fieldV = v[field];

  if (fieldV.required === false) return 'Campo obrigatório';
  if (fieldV.arquivoArpObrigatorio === false) return 'Campo obrigatório';
  if (fieldV.isDate === false) return 'Data inválida';

  if (field === 'dataAssinatura' && fieldV.assinaturaNaoFutura === false) {
    return 'A assinatura não pode ser futura';
  }
  if (field === 'dataVigenciaInicio' && fieldV.vigenciaInicioDepoisOuIgualAssinatura === false) {
    return 'O início da vigência não pode ser anterior à assinatura';
  }
  if (field === 'dataVigenciaFim' && fieldV.vigenciaFimDepoisDoInicio === false) {
    return 'O fim da vigência deve ser posterior ao início';
  }
  if (field === 'dataVigenciaFim' && validacao === 'dados' && fieldV.maxVigencia12Meses === false) {
    return 'Na inclusão, a vigência máxima é de 12 meses a partir da assinatura';
  }
  if (field === 'dataVigenciaFim' && validacao === 'dados' && fieldV.maxVigencia24Meses === false) {
    return 'Na retificação/prorrogação, a vigência total máxima é de 24 meses a partir da assinatura';
  }
  if (field === 'justificativa' && fieldV.justificativaMin10Trim === false) {
    return 'Mínimo 10 caracteres';
  }
  if (field === 'justificativa' && fieldV.maxLength === false) {
    return 'Máximo 255 caracteres (exigência PNCP)';
  }

  return 'Campo inválido';
}

export function obterArquivoParaEnvio(arquivo) {
  if (arquivo == null) return null;
  if (Array.isArray(arquivo)) return arquivo[0] || null;
  return arquivo;
}

/** Documento principal da ARP (tipo PNCP 11). */
export function obterDocumentoPrincipalArp(ataOrDocs) {
  let docs = [];
  if (Array.isArray(ataOrDocs)) {
    docs = ataOrDocs;
  } else if (Array.isArray(ataOrDocs?.documentos)) {
    docs = ataOrDocs.documentos;
  }
  return (
    docs.find(d => {
      const tipo = Number(d.tipoDocumentoId ?? d.tipoDocumento);
      return tipo === TIPO_DOCUMENTO_ARP_PRINCIPAL;
    }) || null
  );
}

/** Nome do arquivo no container `documents` (blob). */
export function obterArquivoPrincipalArp(ataOrDocs) {
  const doc = obterDocumentoPrincipalArp(ataOrDocs);
  return doc?.arquivo || doc?.nomeArquivo || doc?.path || null;
}

/** Rascunho aguardando assinatura (sem dataAssinatura / não PUBLICADA). */
export function isAtaRascunho(ata) {
  if (!ata) return false;
  if (ata.dataAssinatura) return false;
  if (ata.estado === 'PUBLICADA') return false;
  return true;
}

/**
 * Tipo de jornada usado na criação.
 * Preferência: campo `origem` persistido; fallback heurístico.
 */
export function inferirTipoJornadaArp(ata) {
  const origem = String(ata?.origem || '').toUpperCase();
  if (origem === 'VENCEDOR') return 'vencedor';
  if (origem === 'RESERVA') return 'reserva';
  if (origem === 'PROPRIA') return 'propria';

  const fornecedorId = Number(ata?.fornecedorId || ata?.ofertaCompraFornecedorId);
  if (Number.isFinite(fornecedorId) && fornecedorId > 0) return 'vencedor';
  return 'propria';
}

/** Monta draft Vuex a partir de uma ata da lista. */
export function mapAtaParaDraftArp(ata) {
  const tipo = inferirTipoJornadaArp(ata);
  return {
    fornecedorId: ata?.fornecedorId || ata?.ofertaCompraFornecedorId || null,
    fornecedorNome: ata?.fornecedorNome || '',
    papel: tipo === 'reserva' ? 'reserva' : 'vencedor',
    numero: ata?.numero || ata?.numeroAtaRegistroPreco || '',
    ano: ata?.ano || ata?.anoAta || '',
    vigenciaInicio: ata?.dataVigenciaInicio || null,
    vigenciaFim: ata?.dataVigenciaFim || null,
    permitirCarona: Boolean(
      ata?.possibilidadeAdesao === true ||
        ata?.possibilidadeAdesao === 'true' ||
        ata?.possibilidadeAdesao === 1,
    ),
    dataAssinatura: ata?.dataAssinatura || null,
    arquivo: null,
    itensSelecionados: [],
    itensIds: [],
    ataId: ata?.id || null,
    arquivoGerado: obterArquivoPrincipalArp(ata),
    justificativa: '',
  };
}

export function isDiaNaoUtilArp(date, feriadosNacionais = []) {
  const d = parseIsoDateLocal(date);
  if (!d) return true;

  const day = d.getDay();
  if (day === 0 || day === 6) return true;

  return feriadosNacionais.some(feriado => {
    const feriadoDate = parseIsoDateLocal(feriado);
    return feriadoDate != null && feriadoDate.getTime() === d.getTime();
  });
}
